import {Autocomplete, Transition} from '@mantine/core';
import {type FC, useEffect, useRef, useState} from 'react';
import {FaSearch} from 'react-icons/fa';
import {IoSearchCircleOutline} from 'react-icons/io5';
import {useGetGroupedSupportCompanies} from '../../../../core/repositories/companyRepository';
import {CommonLink} from '../../CommonLink';

type Props = {
  shownSearch: boolean;
  showSearch: () => void;
  hideSearch: () => void;
  handleSearchSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
};
export const SearchBar: FC<Props> = ({
  shownSearch,
  showSearch,
  hideSearch,
  handleSearchSubmit,
  searchQuery,
  setSearchQuery,
}) => {
  const defaultDuration = 500;
  const [duration, setDuration] = useState(defaultDuration);
  const searchBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        hideSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hideSearch]);

  const scaleX = {
    in: {opacity: 1, transform: 'scaleX(1)'},
    out: {opacity: 0, transform: 'scaleX(0)', transitionDuration: '0.1s'},
    common: {transformOrigin: 'right'},
    transitionProperty: 'transform, opacity',
  };

  const {data} = useGetGroupedSupportCompanies({
    queryParams: {},
    config: {
      enabled: shownSearch,
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: 30 * 60 * 1000, // キャッシュを30分保持
    },
  });
  const companyNameList = data?.ok
    ? data?.value.companyGroups.flatMap((group) =>
        group.companies.map((company) => company.name),
      )
    : [];

  return (
    <div className="flex items-center" onClick={showSearch} ref={searchBarRef}>
      {!shownSearch && (
        <button aria-label="検索する">
          <IoSearchCircleOutline size={24} />
        </button>
      )}
      <Transition
        mounted={shownSearch}
        transition={scaleX}
        duration={duration}
        timingFunction="ease"
        onEntered={() => {
          setDuration(0);
        }}
      >
        {(styles: React.CSSProperties) => (
          <form onSubmit={handleSearchSubmit} style={styles}>
            <Autocomplete
              type="search"
              icon={<FaSearch />}
              radius="xl"
              size="xs"
              className="text-sm w-[200px]"
              placeholder="企業や募集、コラムを検索"
              data={companyNameList ?? []}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e);
              }}
              itemComponent={(props) => (
                <CommonLink
                  key={`search-company-${props.id}`}
                  href={`/search?q=${props.value}`}
                  className="border-solid border-b-2 border-line last:border-b-0 cursor-pointer flex items-center line-clamp-2 hover:underline text-primary p-4"
                >
                  {props.value}
                </CommonLink>
              )}
            />
          </form>
        )}
      </Transition>
    </div>
  );
};
